import React from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { openBillingPortal } from '../services/subscriptionService';
import { todayLocalISO } from '../utils/dateUtils';

/**
 * The card-decline grace (INV-76, ADR-0048): a paying clinic whose payment failed keeps StableTrack
 * for 7 days. Everyone sees the countdown in the sidebar; the owner, the only one who can update
 * the card, also gets a popup once a day. Both render only while /subscription/status reports days
 * left, so offline (no status) nothing shows.
 */
export function hasPaymentGrace(daysRemaining: number | null | undefined): daysRemaining is number {
  return typeof daysRemaining === 'number' && daysRemaining > 0;
}

const daysLeft = (n: number) => (n === 1 ? '1 day left' : `${n} days left`);

function useBillingPortal() {
  const [opening, setOpening] = React.useState(false);
  const [error, setError] = React.useState('');
  const open = async () => {
    setOpening(true);
    setError('');
    try {
      await openBillingPortal();
    } catch (e: any) {
      const status = e?.response?.status;
      // A 4xx message is for people (no Stripe customer yet); a 5xx one is not.
      const message = status && status < 500 ? e.response.data?.message : null;
      setError(message || 'Could not open the billing page. Please try again.');
      setOpening(false);
    }
  };
  return { open, opening, error };
}

export const PaymentGraceBanner: React.FC<{ daysRemaining: number; canManage: boolean }> = ({
  daysRemaining,
  canManage,
}) => {
  const portal = useBillingPortal();
  return (
    <Box
      role="status"
      sx={(theme) => ({
        mx: 1.5,
        mb: 1,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        bgcolor: alpha(theme.palette.warning.main, 0.08),
        border: '1px solid',
        borderColor: alpha(theme.palette.warning.main, 0.4),
      })}
    >
      <Typography variant="caption" sx={{ display: 'block', color: 'warning.dark', fontWeight: 600, lineHeight: 1.4 }}>
        Payment failed: {daysLeft(daysRemaining)}
      </Typography>
      {canManage ? (
        <Button
          size="small"
          variant="contained"
          onClick={portal.open}
          disabled={portal.opening}
          sx={{ mt: 0.75, textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', py: 0.25 }}
        >
          Update card
        </Button>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 0.5, lineHeight: 1.4 }}>
          Ask your clinic owner to update the card.
        </Typography>
      )}
      {portal.error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {portal.error}
        </Typography>
      )}
    </Box>
  );
};

export const PaymentGraceDialog: React.FC<{ daysRemaining: number; userId: string }> = ({
  daysRemaining,
  userId,
}) => {
  const [open, setOpen] = React.useState(false);
  const portal = useBillingPortal();

  // Once a day: the day is remembered as soon as the popup shows, however it is closed.
  React.useEffect(() => {
    const key = `paymentGracePromptDay:${userId}`;
    const today = todayLocalISO();
    let shownOn: string | null = null;
    try {
      shownOn = localStorage.getItem(key);
    } catch {
      // Storage blocked: show it.
    }
    if (shownOn === today) return;
    setOpen(true);
    try {
      localStorage.setItem(key, today);
    } catch {
      // Nothing to remember it with; it shows again on the next load.
    }
  }, [userId]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs" fullWidth>
      <DialogTitle>Your payment didn't go through</DialogTitle>
      <DialogContent>
        <Typography variant="h4" sx={{ color: 'warning.dark', fontWeight: 700, mb: 1 }}>
          {daysLeft(daysRemaining)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We couldn't charge the card on file for your StableTrack subscription. Update your card
          before the time runs out, or StableTrack will be paused for everyone at your clinic.
        </Typography>
        {portal.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {portal.error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>
          Remind me tomorrow
        </Button>
        <Button variant="contained" onClick={portal.open} disabled={portal.opening} sx={{ textTransform: 'none' }}>
          Update card
        </Button>
      </DialogActions>
    </Dialog>
  );
};
